import React, { useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { selectCurrentUser } from '@shared/store/auth.duck';
import { BodyRegular } from "../../atoms/fonts/Body";
import { setMenuOpen } from '@shared/store/general.duck';
import UserMenu from "./components/user-menu/UserMenu";
import { Accordian } from '@shared/animations/AnimationVariants';
import { SearchBar } from "../search/SearchBar";
import { BrandingLogo } from './components/BrandingLogo';
import { OutlineButton } from "../../atoms/buttons/OutlineButton";
import { isInTeams } from '@shared/utils/TeamsUtils';
import { useTranslation } from "react-i18next";
import { SubHeader } from "./components/sub-headers/SubHeader";
import { selectIsSearchBarVisible } from '@shared/store/dashboard.duck';
import { selectGlobalHeaderCollapsed } from '@features/whereabouts/store/whereaboutsView.duck';
import styled from "styled-components";
import Colours from "../../atoms/Colours";

export default function Header(props: Props) {
  const location = useLocation();
  const pathname = location.pathname;
  const currentUser = useSelector(selectCurrentUser);
  const isAuthenticated = !!currentUser;
  const isSearchBarVisible = useSelector(selectIsSearchBarVisible);
  const globalHeaderCollapsed = useSelector(selectGlobalHeaderCollapsed);
  const dispatch = useDispatch();
  const {t} = useTranslation();
  const isTeamWhereabouts = pathname.startsWith('/whereabouts/team');
  const collapsed = isTeamWhereabouts && globalHeaderCollapsed;

  useEffect(() => {
    if (collapsed) {
      document.body.classList.add('whereabouts-header-collapsed');
      return () => document.body.classList.remove('whereabouts-header-collapsed');
    }
    document.body.classList.remove('whereabouts-header-collapsed');
  }, [collapsed]);

  const isLoginPage = useMemo(() => pathname.includes('/login'), [pathname]);
  const closeMenu = () => dispatch(setMenuOpen(false));

  if (isInTeams()) {
    return (<>
      <div className="header__container">
        {currentUser && <>
          <SubHeader />
        </>}
      </div>
      <Accordian isOpen={isSearchBarVisible}>
        <SearchBar />
      </Accordian>
    </>)
  }

  return (
    <>
      <div className={props.fullScreenView ? 'header__container--fullscreen' : 'header__container'}>
        <HeaderBackground collapsed={collapsed}>
          <div className="header row">
            <BrandingLogo />

            {!isAuthenticated && !isLoginPage ? <nav className="row navigation">
              <OutlineButton className="navigation__item navigation__item--login" link={"/login"} text={"general.login"} />
            </nav> : null}

            {(isAuthenticated && !currentUser?.runOnboarding) ? <nav className="subnav subnav--desktop row">
              <div className={`subnav__item ${location.pathname.includes('/whereabouts/') && !location.pathname.includes('/whereabouts/company') ? 'subnav__item--selected' : ''}`}>
                <Link to="/whereabouts/team" onClick={closeMenu} data-test="header-teamWhereabouts-link">
                  <BodyRegular weight={600}>{t('team-view.whereabouts')}</BodyRegular>
                </Link>
              </div>
              <div className={`subnav__item ${location.pathname.includes('/whereabouts/company') ? 'subnav__item--selected' : ''}`}>
                <Link to="/whereabouts/company/all" onClick={closeMenu} data-test="header-companyWide-link">
                  <BodyRegular weight={600}>{t('button.company-view')}</BodyRegular>
                </Link>
              </div>
              {currentUser?.companyEntity?.enableHolidayBooking && <div className={`subnav__item ${location.pathname.startsWith('/holidays') ? 'subnav__item--selected' : ''}`}>
                <Link to="/holidays/leave-planner" onClick={closeMenu} data-test="header-holidays-link">
                  <BodyRegular weight={600}>{t('header.leave-planner')}</BodyRegular>
                </Link>
              </div>}
              <div className={`subnav__item ${location.pathname.startsWith('/meeting-rooms') ? 'subnav__item--selected' : ''}`}>
                <Link to="/meeting-rooms" onClick={closeMenu} data-test="header-workplanner-link">
                  <BodyRegular weight={600}>{t('header.meeting-rooms')}</BodyRegular>
                </Link>
              </div>
            </nav> : null}

            {!!currentUser ? <UserMenu /> : null}
          </div>
          <SubHeader />
        </HeaderBackground>
        <Accordian isOpen={isSearchBarVisible}>
          <SearchBar />
        </Accordian>
      </div>
    </>
  );
}

interface Props {
  fullScreenView?: boolean;
}

const HeaderBackground = styled.div<{ collapsed?: boolean }>`
  background-color: ${Colours.veryDarkBlue};
  overflow: hidden;
  max-height: ${props => props.collapsed ? '0' : '400px'};
  transition: max-height 0.2s ease;
`
